import styled, { css } from "styled-components";
import Text from "common/Typography/Text";
import Divider from "antd/es/divider";
import CardSorters from "common/CardSorters/CardSorters";
import { ArrowRightOutlined } from "common/Icons/Icons";
import { buttonStyles, StyledDefaultButton } from "common/Buttons/Button";
import { hexToRGB } from "@shared/utils/misc";
import DebouncedButton from "common/Buttons/DebouncedButton";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

export const FormFieldTitle = styled(Text).attrs<{ $disabled?: boolean }>({
  as: "div",
})`
  margin-bottom: 8px;
  display: flex;
  align-items: center;

  ${(p) =>
    p.onClick &&
    `
  cursor: pointer;
  `}

  ${(p) =>
    p.$disabled &&
    `
    opacity: 0.5;
  `}

  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The label role: 12.5/16, 500, secondary (never faded). */
      ${newLookType.label};
      color: ${getNewLookTokens(p.theme).secondary};
      gap: 4px;
    `}
`;

export const SummaryFieldTitle = styled(Text).attrs({
  type: "secondary",
})`
  margin-bottom: 6px;
`;

export const FormDivider = styled(Divider)`
  border-color: ${(p) => p.theme.colors.lightBorderGrey};
  margin: 20px 0;
  ${(p) =>
    p.theme.isNewLook &&
    `
    &.ant-divider {
      border-color: ${p.theme.colors.cardBorderColor};
      margin: 16px 0;
    }
  `}
`;

export const StyledCardSorters = styled(CardSorters)<{ $editable?: boolean }>`
  margin-bottom: 12px;
  padding: 8px 12px;
  ${(p) =>
    p.theme.isNewLook &&
    (p.$editable
      ? css`
          /* Header labels line up with the text inside the order fields. */
          background: transparent;
          margin-bottom: 6px;
          padding: 0 28px 0 0;
          .ant-col {
            padding-left: 9px;
          }
          .ant-col:first-child {
            padding-left: 35px;
          }
        `
      : css`
          /* Read-only rows: labels line up with the values after the number. */
          background: transparent;
          margin-bottom: 6px;
          padding: 4px 0;
          .ant-col:first-child {
            padding-left: 26px;
          }
        `)}
`;

export const Arrow = styled(ArrowRightOutlined)<{ $expanded?: boolean }>`
  margin-left: 4px;
  fill: ${(p) => p.theme.colors.secondaryText};
  transform: rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  transition: transform 0.35s;
  width: 20px;
  height: 20px;
`;

export const ConfirmButton = styled(DebouncedButton).attrs({
  type: "primary",
  size: "large",
})<{
  $disabled: boolean;
}>`
  ${(p) =>
    p.$disabled &&
    `
  ${buttonStyles.antBtnDisabled};
  `};
`;

export const Underline = styled.div<{ $danger?: boolean; $warning?: boolean }>`
  display: flex;
  border-bottom: 1px solid ${(p) => hexToRGB(p.theme.colors.secondaryText, 0.5)};
  margin-right: 12px;
  overflow: hidden;
  ${(p) => {
    if (!p.$danger && !p.$warning) return;
    const color = p.$danger ? p.theme.colors.red : p.theme.colors.gold;
    return `
    border-bottom: 1px solid ${hexToRGB(color, 0.5)};
    color: ${color};
    input {
        color: ${color};
    }
  `;
  }}
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* New look: a compact field (not an underline) for inline order values. */
      flex: 1;
      min-width: 0;
      align-items: center;
      height: 30px;
      padding: 0 8px;
      margin-right: 8px;
      border: 1px solid
        ${p.$danger
          ? p.theme.colors.red
          : p.$warning
          ? hexToRGB(p.theme.colors.gold, 0.7)
          : p.theme.colors.lightBorderGrey};
      border-radius: 8px;
      background: ${p.theme.colors.cardBgColor};
      transition: border-color 150ms, box-shadow 150ms;
      font-variant-numeric: tabular-nums;
      ${!p.$danger &&
      !p.$warning &&
      css`
        &:hover {
          border-color: ${p.theme.colors.mediumBorderGrey};
        }
      `}
      &:focus-within {
        border-color: ${p.$danger
          ? p.theme.colors.red
          : p.theme.colors.primaryBlue};
        box-shadow: 0 0 0 3px
          ${hexToRGB(
            p.$danger ? p.theme.colors.red : p.theme.colors.primaryBlue,
            0.14
          )};
      }
      input {
        background: transparent !important;
      }
      > .ant-typography {
        color: ${p.theme.colors.secondaryText};
        font-size: 12.5px;
      }
    `}
`;

export const Value = styled(Text)<{ $danger?: boolean; $warning?: boolean }>`
  ${(p) => p.theme.isNewLook && `font-variant-numeric: tabular-nums;`}
  ${(p) =>
    p.$danger &&
    `
    color: ${p.theme.colors.red};
  `}
  ${(p) =>
    p.$warning &&
    `
    color: ${p.theme.colors.gold};
  `}
`;

export const IconButton = styled(StyledDefaultButton)`
  height: 40px;
  width: 40px;
  padding: 0 4px;
  font-size: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    flex: none;
    &.ant-btn-default {
      background: transparent !important;
      border: 1px solid ${p.theme.colors.lightBorderGrey};
      color: ${p.theme.colors.secondaryText} !important;
    }
    &.ant-btn-default:not(:disabled):hover {
      background: ${p.theme.colors.cardLightBgColor} !important;
      border-color: ${p.theme.colors.mediumBorderGrey};
      color: ${p.theme.colors.primaryText} !important;
    }
  `}
`;
